import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Battery,
  Smartphone,
  Activity as ActivityIcon,
  AlertTriangle,
  TrendingUp,
  Wifi,
  WifiOff,
} from "lucide-react";
import { api } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Empty } from "@/components/ui/empty";

export function Fleet() {
  const statusQ = useQuery({
    queryKey: ["fleet", "status"],
    queryFn: () => api.fleetStatus(),
    refetchInterval: 30_000,
  });
  const queueQ = useQuery({
    queryKey: ["fleet", "queue"],
    queryFn: () => api.fleetQueue(),
    refetchInterval: 30_000,
  });
  const replyQ = useQuery({
    queryKey: ["fleet", "reply_rate"],
    queryFn: () => api.fleetReplyRate(7),
    refetchInterval: 60_000,
  });
  const candQ = useQuery({
    queryKey: ["fleet", "candidates"],
    queryFn: () => api.fleetCandidatesSummary(),
    refetchInterval: 5 * 60_000,
  });

  const summary = statusQ.data?.summary;
  const devices = statusQ.data?.devices ?? [];

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Recruiting Fleet</h1>
        <p className="text-sm text-muted-foreground">
          UV Creator Tools iOS devices — IG cold outreach. Polled every 30s.
        </p>
      </header>

      {/* Summary KPIs */}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        <Kpi
          label="Online"
          value={
            statusQ.isLoading ? "—" : `${summary?.online_devices ?? 0}/${summary?.total_devices ?? 0}`
          }
          icon={<Smartphone className="h-4 w-4" />}
          tone={summary && summary.offline_devices > 0 ? "warn" : "neutral"}
        />
        <Kpi
          label="Sent today"
          value={String(summary?.total_sent_today ?? "—")}
          icon={<TrendingUp className="h-4 w-4" />}
        />
        <Kpi
          label="Utilization"
          value={summary ? `${summary.utilization_pct}%` : "—"}
          icon={<ActivityIcon className="h-4 w-4" />}
          tone={
            summary && summary.utilization_pct < 30
              ? "warn"
              : summary && summary.utilization_pct > 95
                ? "warn"
                : "neutral"
          }
        />
        <Kpi
          label="Barb pending"
          value={String(summary?.barb_pending ?? "—")}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone={summary && summary.barb_pending > 5 ? "warn" : "neutral"}
        />
        <Kpi
          label="Capacity"
          value={String(summary?.total_capacity_today ?? "—")}
          icon={<TrendingUp className="h-4 w-4" />}
        />
      </div>

      {/* Devices */}
      <section>
        <h2 className="mb-2 text-sm font-medium">Devices</h2>
        {statusQ.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : devices.length === 0 ? (
          <Empty title="No devices reporting." />
        ) : (
          <div className="overflow-hidden rounded-md border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-2xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left">Recruiter</th>
                  <th className="px-3 py-1.5 text-right">Daily</th>
                  <th className="px-3 py-1.5 text-right">Hourly</th>
                  <th className="px-3 py-1.5 text-right">Util</th>
                  <th className="px-3 py-1.5 text-right">Barb pending</th>
                  <th className="px-3 py-1.5 text-right">Battery</th>
                  <th className="px-3 py-1.5 text-right">Last seen</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {devices.map((d) => (
                  <tr key={d.recruiter_name + (d.device_id || "")}>
                    <td className="px-3 py-1.5">
                      <div className="flex items-center gap-2">
                        {d.online ? (
                          <Wifi className="h-3 w-3 text-emerald-600" />
                        ) : (
                          <WifiOff className="h-3 w-3 text-rose-500" />
                        )}
                        <span className="font-medium">{d.recruiter_name}</span>
                        {d.is_sending && (
                          <span className="rounded-full bg-blue-100 px-1.5 text-2xs text-blue-700">
                            sending
                          </span>
                        )}
                        {d.barb_monitoring && (
                          <span className="rounded-full bg-violet-100 px-1.5 text-2xs text-violet-700">
                            Barb
                          </span>
                        )}
                      </div>
                      <div className="ml-5 mt-0.5 text-2xs text-muted-foreground">
                        {d.app_version || "—"} · {d.device_id || "—"}
                      </div>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-xs">
                      {d.daily_sent}/{d.daily_limit}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-xs">
                      {d.hourly_sent}/{d.hourly_limit}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-xs">
                      <span
                        className={
                          d.utilization_pct < 30
                            ? "text-amber-700"
                            : d.utilization_pct > 95
                              ? "text-rose-600"
                              : ""
                        }
                      >
                        {d.utilization_pct}%
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-xs">
                      <span className={d.barb_pending > 5 ? "text-amber-700" : ""}>
                        {d.barb_pending}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-xs">
                      {d.battery != null ? (
                        <span className="inline-flex items-center gap-1">
                          <Battery
                            className={
                              "h-3 w-3 " +
                              (d.battery < 20 ? "text-rose-500" : "text-muted-foreground")
                            }
                          />
                          {d.battery}%
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-2xs text-muted-foreground">
                      {d.seconds_since_heartbeat != null
                        ? secsAgo(d.seconds_since_heartbeat)
                        : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Reply rate */}
        <section>
          <h2 className="mb-2 text-sm font-medium">Reply rate (7d)</h2>
          {replyQ.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (replyQ.data?.items ?? []).length === 0 ? (
            <Empty title="No DMs sent in window." />
          ) : (
            <div className="overflow-hidden rounded-md border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-2xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Recruiter</th>
                    <th className="px-3 py-1.5 text-right">Sent</th>
                    <th className="px-3 py-1.5 text-right">Replies</th>
                    <th className="px-3 py-1.5 text-right">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(replyQ.data?.items ?? []).map((r) => (
                    <tr key={r.recruiter_name}>
                      <td className="px-3 py-1.5">{r.recruiter_name}</td>
                      <td className="px-3 py-1.5 text-right tnum">{r.sent}</td>
                      <td className="px-3 py-1.5 text-right tnum">{r.replied}</td>
                      <td className="px-3 py-1.5 text-right tnum">
                        <span className={r.reply_rate_pct > 15 ? "text-emerald-600" : ""}>
                          {r.reply_rate_pct.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Send queue depth */}
        <section>
          <h2 className="mb-2 text-sm font-medium">Send queue (24h)</h2>
          {queueQ.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : (queueQ.data?.by_recruiter ?? []).length === 0 ? (
            <Empty title="Queue is empty." />
          ) : (
            <div className="overflow-hidden rounded-md border bg-card">
              <table className="w-full text-sm">
                <thead className="bg-muted/30 text-2xs uppercase text-muted-foreground">
                  <tr>
                    <th className="px-3 py-1.5 text-left">Recruiter (IG handle)</th>
                    <th className="px-3 py-1.5 text-right">Pending</th>
                    <th className="px-3 py-1.5 text-right">Sent (1h)</th>
                    <th className="px-3 py-1.5 text-right">Failed (1h)</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(queueQ.data?.by_recruiter ?? []).map((r) => (
                    <tr key={r.assigned_recruiter_name}>
                      <td className="px-3 py-1.5 font-mono text-xs">
                        {r.assigned_recruiter_name}
                      </td>
                      <td className="px-3 py-1.5 text-right tnum">{r.pending}</td>
                      <td className="px-3 py-1.5 text-right tnum text-emerald-700">
                        {r.sent_last_hour}
                      </td>
                      <td className="px-3 py-1.5 text-right tnum">
                        <span className={r.failed_last_hour > 0 ? "text-rose-600" : ""}>
                          {r.failed_last_hour}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Candidates pool */}
        <section>
          <h2 className="mb-2 text-sm font-medium">Candidate pool</h2>
          {candQ.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (
            <div className="grid gap-2">
              {(candQ.data?.items ?? []).map((c) => (
                <div
                  key={c.tier}
                  className="flex items-baseline justify-between rounded-md border bg-card px-3 py-2"
                >
                  <span className="text-sm">{c.tier}</span>
                  <span className="text-xs text-muted-foreground">
                    <span className="tnum text-foreground font-semibold">
                      {c.unassigned}
                    </span>{" "}
                    unassigned of {c.total}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Recent failures */}
        <section>
          <h2 className="mb-2 text-sm font-medium">Recent failure reasons (24h)</h2>
          {queueQ.isLoading ? (
            <Skeleton className="h-24 w-full" />
          ) : (queueQ.data?.recent_failures ?? []).length === 0 ? (
            <Empty title="No failures in window." />
          ) : (
            <div className="grid gap-1">
              {(queueQ.data?.recent_failures ?? []).map((f) => (
                <div
                  key={f.error}
                  className="flex items-baseline justify-between rounded-md border bg-card px-3 py-1.5 text-xs"
                >
                  <span className="font-mono">{f.error}</span>
                  <span className="tnum text-muted-foreground">{f.count}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({
  label,
  value,
  icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  tone?: "neutral" | "warn" | "danger";
}) {
  const accent =
    tone === "danger"
      ? "text-rose-600"
      : tone === "warn"
        ? "text-amber-700"
        : "";
  return (
    <div className="rounded-md border bg-card p-3">
      <div className="flex items-center gap-1.5 text-2xs uppercase text-muted-foreground">
        {icon} {label}
      </div>
      <div className={`text-xl font-semibold mt-0.5 ${accent}`}>{value}</div>
    </div>
  );
}

function secsAgo(s: number): string {
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  return `${Math.floor(h / 24)}d`;
}
